<template>
  <div class="row">
    <div class="col-sm-12 col-md-12 col-lg-8">
      <div class="video-wrapper" ref="videoWrapper">
        <template v-if="isLocked">
          <img v-if="lockedVideoInfo.thumbnail" :src="lockedVideoInfo.thumbnail" class="locked-thumbnail" />
          <paywall-overlay
            :video="lockedVideoInfo"
            @onBuy="onBuy"
            @onUpgrade="onUpgrade"
          />
        </template>
        <template v-else>
          <div ref="player"></div>
          <iframe
            v-if="!isYoutubeVideo"
            :src="video.url"
            ref="iframe"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            referrerpolicy="strict-origin-when-cross-origin">
          </iframe>
        </template>
      </div>
      <h2 class="video-title">{{ video.title }}</h2>
      <video-header
        :category="category"
        :video="video"
        @handleLikeVideo="handleLikeVideo"
      />
      <video-description :video="video" @seekTo="handleSeek" class="mr-b-10" />
      <h3 class="font-bold">{{ video.comments }} 件のコメント</h3>
      <div ref="videoCommentsWrapper" class="video-comments-wrapper">
        <video-comments
          :video="video"
          :comments="comments"
          :sellerId="sellerId"
          @submitComment="submitComment"
          @updateComment="updateComment"
          @handleLikeComment="handleLikeComment"
          @submitReply="submitReply"
          @seekTo="handleSeek"
        />
      </div>
    </div>
    <div class="col-sm-12 col-md-12 col-lg-4">
      <div v-if="isLocked && lockedVideoInfo.price" class="ppv-panel">
        <div class="ppv-header">
          <i aria-hidden="true" class="fa fa-diamond"></i>
          プレミアム限定動画
        </div>
        <label class="ppv-option" :class="{ selected: ppvOption === 'ppv' }">
          <input type="radio" v-model="ppvOption" value="ppv" />
          <div class="ppv-option-body">
            <div class="ppv-option-title">この動画を購入</div>
            <div class="ppv-option-price">
              <!-- <span class="ppv-pretax">税抜 </span> -->
              <span class="ppv-tax-included">
                {{ formatPrice(lockedVideoInfo.price) }}円
                <span class="ppv-tax-label">（税込 {{ formatPrice(lockedVideoInfo.totalPrice) }} 円）</span>
              </span>
            </div>
          </div>
        </label>
        <label class="ppv-option" :class="{ selected: ppvOption === 'upgrade' }">
          <input type="radio" v-model="ppvOption" value="upgrade" />
          <div class="ppv-option-body">
            <div class="ppv-option-title">プレミアムに登録</div>
            <div class="ppv-option-price">
              <span class="ppv-pretax">全てのプレミアム動画が見放題</span>
              <span class="ppv-tax-included">{{ formattedPriceExTaxMonth }}円<span class="ppv-tax-label">/月（税込 {{ formattedPriceInTaxMonth }} 円）</span></span>
            </div>
          </div>
        </label>
        <button v-if="ppvOption === 'ppv'" class="btn btn-primary" @click="onBuy">
          <i class="fa fa-shopping-cart" aria-hidden="true"></i>
          動画を購入
        </button>
        <button v-else class="btn btn-upgrade-premium" @click="onUpgrade">
          <i class="fa fa-diamond" aria-hidden="true"></i>
          プレミアムに登録
        </button>
        <span v-if="ppvOption === 'ppv'">※「動画を購入」を押すと、Stripeの決済画面へ移動します。</span>
      </div>
      <related-videos
        :videos="videos"
        :categoryId="categoryId"
        ref="relatedVideos"
        @handleClickViewVideo="$emit('handleClickViewVideo', $event)"
      />
    </div>
    <modal-alert
      v-if="isShowModal"
      :message="message"
      @close="closeModal"
    />
  </div>
</template>
<script>
import Videoservice from '../../services/videoservice'
import MemberPlanService from '@/services/memberplanservice'
import Util from '@/common/utility'
import LikeButton from './components/LikeButton'
import VideoHeader from './components/VideoHeader'
import VideoDescription from './components/VideoDescription'
import VideoComments from './components/VideoComments'
import RelatedVideos from './components/RelatedVideos'
import PaywallOverlay from './PaywallOverlay'
import ModalAlert from '@/components/ModalAlert'
import EventService from '@/services/eventservice'
import eventCode from '@/common/event-code'

export default {
  components: {
    LikeButton,
    VideoHeader,
    VideoDescription,
    VideoComments,
    RelatedVideos,
    PaywallOverlay,
    ModalAlert
  },
  data () {
    return {
      loader: null,
      loaderComments: null,
      player: null,
      isPlayerReady: false,
      comments: [],
      isLocked: false,
      lockedVideoInfo: {},
      ppvOption: 'ppv',
      isShowModal: false,
      message: '動画の購入をキャンセルしました',
      priceExTaxMonth: null,
      taxRate: null
    }
  },
  computed: {
    sellerId () {
      return this.$store.getters.getUser.sellerId
    },
    videos () {
      return this.$store.getters.getVideos
    },
    videoId () {
      return Number(this.$route.params.id)
    },
    video () {
      return this.videos.find(v => v.id === this.videoId)
    },
    categoryId () {
      return this.video.category
    },
    categories () {
      return this.$store.getters.getCategories
    },
    category () {
      return this.categories.find(i => { return i.id === this.categoryId })
    },
    isYoutubeVideo () {
      if (!this.video || !this.video.url) return false
      const u = new URL(this.video.url)
      return u.hostname.includes('youtube')
    },
    formattedPriceExTaxMonth () {
      return this.priceExTaxMonth != null ? Util.formatMoney(this.priceExTaxMonth) : '-'
    },
    formattedPriceInTaxMonth () {
      if (this.priceExTaxMonth == null || this.taxRate == null) return '-'
      return Util.formatMoney(Math.round(parseFloat(this.priceExTaxMonth) * (1 + parseFloat(this.taxRate))))
    }
  },
  async mounted () {
    if (!this.videoId) {
      this.$router.push('/not-found')
      return
    }

    const payment = this.$route.query.payment
    if (payment === 'canceled') {
      this.message = '動画の購入をキャンセルしました'
      this.isShowModal = true
      this.$router.replace({ query: { ...this.$route.query, payment: undefined } })
    } else if (payment === 'success') {
      this.message = '動画の購入が完了しました'
      this.isShowModal = true
      this.$router.replace({ query: { ...this.$route.query, payment: undefined } })
    }

    MemberPlanService.getMemberPlanInfo().then(records => {
      this.priceExTaxMonth = records.price_ex_tax_month
      this.taxRate = records.tax_rate
    }).catch(() => {})

    this.loader = this.$loading.show()
    await this.init()
    if (!this.isLocked) {
      await this.initYouTubePlayer()
    }
    this.loader && this.loader.hide()

    this.syncPlaylistHeight()
    window.addEventListener('resize', this.syncPlaylistHeight)
  },
  methods: {
    async init () {
      const logParams = {
        name: eventCode.video.play,
        properties: {
          videoId: this.videoId
        }
      }
      EventService.postTrackLog(logParams)
      await this.getVideo()
      this.getComments()
    },
    async initYouTubePlayer () {
      if (!this.isYoutubeVideo) return

      if (this.player && this.isPlayerReady) return

      const videoId = this.extractVideoId(this.video.url)
      const startAt = parseInt(this.$route.query.t) || 0
      const mute = parseInt(this.$route.query.mute) || 0

      this.player = new window.YT.Player(this.$refs.player, {
        videoId,
        playerVars: {
          origin: window.location.origin,
          playsinline: 1,
          autoplay: 1,
          mute: mute,
          ...(startAt > 0 && { start: startAt })
        },
        events: {
          onReady: this.onPlayerReady
        }
      })
    },
    closeModal () {
      this.isShowModal = false
      window.location.reload()
    },
    extractVideoId (url) {
      const u = new URL(url)
      if (u.hostname.includes('youtube')) {
        return u.pathname.slice(7)
      }
      return u.searchParams.get('v')
    },
    onPlayerReady () {
      this.isPlayerReady = true
    },
    loadingComments () {
      this.loaderComments && this.loaderComments.hide()
      this.loaderComments = this.$loading.show({
        container: this.$refs.videoCommentsWrapper,
        backgroundColor: 'rgba(255,255,255,0.7)',
        canCancel: false,
        width: 40,
        height: 40
      })
    },
    getComments () {
      this.loadingComments()
      this.comments = []
      Videoservice.getComments({ videoId: this.videoId })
        .then((response) => {
          this.comments = response.data
        })
        .catch((error) => {
          this.$events.fire('handleError', error)
        })
        .finally(() => {
          this.loaderComments && this.loaderComments.hide()
        })
    },
    async getVideo () {
      await Videoservice.getVideo({ videoId: this.videoId })
        .then((response) => {
          if (response.locked) {
            this.isLocked = true
            this.lockedVideoInfo = {
              id: this.videoId,
              title: response.title || (this.video && this.video.title) || '',
              thumbnail: response.thumbnail || (this.video && this.video.thumbnail) || '',
              price: response.price,
              totalPrice: response.totalPrice
            }
            return
          }
          this.isLocked = false
          if (!this.video.url) {
            this.video.url = response.url
          }
          this.video.enableLiveChat = response.enableLiveChat
          this.video.liked = response.liked
          this.video.likes = response.likes
          this.video.comments = response.comments
        })
        .catch((error) => {
          this.$events.fire('handleError', error)
        })
    },
    syncPlaylistHeight () {
      this.$nextTick(() => {
        const wrapper = this.$refs.videoWrapper
        const playlist = this.$refs.relatedVideos.$refs.playList
        const liveChat = this.$refs.relatedVideos.$refs.liveChat
        if (wrapper && playlist) {
          if (window.innerWidth > 1024) {
            playlist.style.maxHeight = wrapper.clientHeight + 'px'
          } else {
            playlist.style.maxHeight = 'unset'
          }
        }

        if (wrapper && liveChat) {
          if (window.innerWidth > 1024) {
            liveChat.style.height = wrapper.clientHeight + 'px'
          } else {
            liveChat.style.removeProperty('height')
          }
        }
      })
    },
    handleLikeVideo () {
      if (!this.video.liked) {
        const logParams = {
          name: eventCode.video.like,
          properties: {
            videoId: this.videoId
          }
        }
        EventService.postTrackLog(logParams)
        this.video.likes += 1
        this.video.liked = true
        Videoservice.likeVideo({ videoId: this.videoId })
      }
    },
    async submitComment (newComment) {
      this.loadingComments()
      const logParams = {
        name: eventCode.video.comment,
        properties: {}
      }
      EventService.postTrackLog(logParams)

      await Videoservice.postComment({ videoId: this.videoId, content: newComment })
        .then((response) => {
          this.comments.unshift(response.data)
          this.video.comments += 1
        })
        .catch((error) => {
          this.$events.fire('handleError', error)
        })
        .finally(() => {
          this.loaderComments && this.loaderComments.hide()
        })
    },
    async updateComment (comment) {
      this.loadingComments()
      const logParams = {
        name: eventCode.video.comment,
        properties: {}
      }
      EventService.postTrackLog(logParams)

      await Videoservice.putComment({ commentId: comment.id, content: comment.content })
        .then((response) => {
        })
        .catch((error) => {
          this.$events.fire('handleError', error)
        })
        .finally(() => {
          this.loaderComments && this.loaderComments.hide()
        })
    },
    async submitReply (comment, content) {
      this.loadingComments()
      const logParams = {
        name: eventCode.video.comment,
        properties: {}
      }
      EventService.postTrackLog(logParams)

      await Videoservice.postComment({ videoId: this.videoId, content: content, parentId: comment.id })
        .then((response) => {
          comment.replies.push(response.data)
          comment.isShowReplies = true
          this.video.comments += 1
        })
        .catch((error) => {
          this.$events.fire('handleError', error)
        })
        .finally(() => {
          this.loaderComments && this.loaderComments.hide()
        })
    },
    handleLikeComment (comment) {
      if (!comment.liked) {
        const logParams = {
          name: eventCode.video.like_comment,
          properties: {}
        }
        EventService.postTrackLog(logParams)

        comment.likes += 1
        comment.liked = true
        Videoservice.likeComment({ commentId: comment.id })
      }
    },
    formatPrice (price) {
      return Number(price).toLocaleString('ja-JP')
    },
    async onBuy () {
      const logParams = {
        name: eventCode.video.video_purchased,
        properties: {
          videoId: this.videoId
        }
      }
      EventService.postTrackLog(logParams)

      try {
        this.loader = this.$loading.show()
        const response = await Videoservice.createVideoCheckoutSession({ videoId: this.video.id })
        if (response) {
          window.location.href = response.data
        }
      } catch (err) {
        this.$events.fire('handleError', err)
      } finally {
        this.loader && this.loader.hide()
      }
    },
    async onUpgrade () {
      const logParams = {
        name: eventCode.video.upgrade_premium,
        properties: {
          videoId: this.videoId
        }
      }
      EventService.postTrackLog(logParams)

      this.$router.push('/setting/plan-info')
    },
    handleSeek (seconds) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          t: seconds
        }
      })
      this.seekTo(seconds)
    },
    seekTo (seconds) {
      if (!this.player || !this.isPlayerReady) return
      this.player.seekTo(seconds, true)
    }
  },
  watch: {
    async videoId () {
      if (!this.video) {
        this.$router.push('/not-found')
      } else {
        if (this.player && this.isPlayerReady) {
          this.player.pauseVideo()
        }

        if (!this.isYoutubeVideo && this.player) {
          this.player.destroy()
          this.player = null
          this.isPlayerReady = false
        }
        this.loader = this.$loading.show()
        await this.init()
        await this.initYouTubePlayer()
        this.loader && this.loader.hide()

        if (!this.player || !this.isPlayerReady) return
        const videoId = this.extractVideoId(this.video.url)
        this.player.loadVideoById(videoId)
      }
    }
  },
  beforeDestroy () {
    this.loader && this.loader.hide()
    if (this.player) {
      this.player.destroy()
    }
    window.removeEventListener('resize', this.syncPlaylistHeight)
  }
}
</script>
<style>
.video-wrapper iframe {
  width: 100%;
  height: 100%;
  border: none;
}
</style>
<style scoped>
i.fa-diamond {
  animation: rotateY 2s linear infinite;
}
.hpanel {
  background-color: #fff;
  padding: 20px;
}
.video-wrapper {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  position: relative;
}
.locked-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  inset: 0;
}
.video-title {
  font-size: 18px;
  font-weight: bold;
  margin: 16px 0 8px;
}
.video-comments-wrapper {
  position: relative;
}
.ppv-panel {
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}
.ppv-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #a07800;
  letter-spacing: 0.03em;
  padding-bottom: 6px;
  border-bottom: 1px solid #eee;
}
.ppv-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  input[type="radio"] {
    margin-top: 3px;
    flex-shrink: 0;
    accent-color: #1a56db;
  }
  &.selected {
    border-color: #1a56db;
    background: #f0f5ff;
  }
  &:hover:not(.selected) {
    border-color: #aaa;
  }
}
.ppv-option-body {
  flex: 1;
}
.ppv-option-title {
  font-size: 14px;
  font-weight: 600;
  color: #222;
  margin-bottom: 4px;
}
.ppv-option-price {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.ppv-pretax {
  font-size: 12px;
  color: #888;
}
.ppv-tax-included {
  font-size: 20px;
  font-weight: 700;
  color: #111;
  line-height: 1.2;
}
.ppv-tax-label {
  font-size: 12px;
  font-weight: 400;
  color: #666;
}
.btn-upgrade-premium {
  background-color: rgba(207, 155, 0, 0.8);
  color: #000;
  font-size: 14px;
  padding: 5px;
  border-radius: 4px;
  z-index: 2;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/view/video/VideoPlayer.vue